import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toastr } from 'react-redux-toastr';
import {
    Grid,
    Checkbox,
    IconButton,
    Tooltip,
    TextField,
    Typography,
    DialogTitle,
    DialogContent,
    DialogContentText,
    Divider,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { Icon as MdiIcon } from '@mdi/react';
import {
    mdiRefresh,
    mdiAutorenew,
    mdiClipboardOutline,
    mdiAccountReactivate,
} from '@mdi/js';
import { useDispatch, useSelector } from 'react-redux';
import {
    CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
    CheckBox as CheckBoxIcon,
} from '@material-ui/icons';
import { ZaddonsCircularProgress, ColumnFilter } from '@commonClient2/components';
import { dynamicSort } from '@commonClient2/services/sort';
import SplitButton from '@commonClient2/components/SplitButton';
import { UPDATE_WORKPLAN_PUBLISH_PROGRESS } from '@manager/store/workplanPublishProgress';
import { getStepInfo } from '../../store/workplanPublishProgress/workplanPublishProgress.helper';
import * as api from '../../services/api-endpoints/bidProfile.service';
// eslint-disable-next-line no-unused-vars
import { GroupedIconButton, EmployeeReport, ProgressSidePanel } from '../../../components';
import {
    ManagerApprovalAction, EmployeeBiddingStatus, BidProfileStatus, BidProfileEditTab,
    EmployeeReportType, BidProcessType, PeriodType, AwardEmployeesSteps, PublishStatusType
} from '../../../Enums';
import { getEmployeeSubmissionStatusLabel, getAwardingStepCodeLabel } from '../../../Enums/Helpers/LabellingHelper';
import {
    approveBids,
    refuseBids,
    calculateAwarding,
    recalculateEmployeeRanks,
    refuseAndCalculate,
    refreshBidDashboard,
    updateAwardingCompletedBidProfile,
} from '../../store/bidProfileManagement';
import { RESET_EMPLOYEE_PREFERENCES_REPORT, UPDATE_EMPLOYEE_PREFERENCES_REPORT } from '../../../store/employeePreferencesReport';
import { UPDATE_MANAGER_APP_DATA } from '../../store/appData';
import EmployeeSelectionsTable from './EmployeeSelectionsTable';
import OnBehalfShiftlineSelection from './OnBehalfShiftlineSelection';
import { getScheduledTime } from '../../../services/bidProfile';
import { ConfirmDialog } from './ConfirmDialog';
import './BidDashboard.scss';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

/** @typedef {'asc'|'desc'} SortType */
/** @type {SortType} */
const defaultSortType = 'asc';

const employeeSubmissionStatus = Object.values(EmployeeBiddingStatus);

function BidDashboard() {
    const dispatch = useDispatch();
    const { t } = useTranslation('shiftBidding');
    const { t: tBidDashboard } = useTranslation('shiftBidding', { keyPrefix: 'manager.bidProfileEdit.bidDashboard' });
    /** @type {ShiftBidding.ManagerStore} */
    // @ts-ignore
    const {
        bidProfileManagement,
        bidProfile,
        managerAppData,
        employeePreferencesReport,
        // @ts-ignore
        pendingTasks,
        workplanPublishProgress,
    } = useSelector((state) => state);

    const {
        awardingResults,
        expandedRowKeys,
        checkedKeys,
        employeesBidSelection,
        isRecalculateEmployeeRanksAllowed,
        isAwardingEmployees,
    } = bidProfileManagement;

    const {
        profileId,
        status,
        scheduleTemplates,
        originalBidProfileInformation,
        originalSchedule,
        anchorDay,
        isRebid,
        isPublishingToWorkplan,
        isManagementAllowed,
        isViewAllowed,
    } = bidProfile;

    const {
        items, 
        dayCount,
        periodType,
    } = scheduleTemplates;

    const {
        dataviewFields, 
        hyperfinds, 
        managerBidSetups, 
        dataviewEmployeeData, 
        filteredDataviewEmployeeData,
    } = managerAppData;

    const { filters } = employeePreferencesReport;

    const {
        effectiveDate,
        endDate,
        isForeverRequestPeriod,
        progressValue,
        workplanName,
        status: workplanPublishProgressStatus, 
        stepInfo: originalStepInfo,
        isProgressSidePanelOpen,
        progressStepCode,
    } = workplanPublishProgress;

    const stepInfo = progressStepCode ? getStepInfo(progressStepCode, isRebid, bidProfileManagement) : originalStepInfo;
    const hasErrorsInSteps = stepInfo && stepInfo.find((x) => x.errors && x.errors.length >= 1);
    const publishStatusType = hasErrorsInSteps ? PublishStatusType.Error : workplanPublishProgressStatus;

    /** @type {[number[], Function]} StatusFilterSelected */
    const [statusFilterSelected, setStatusFilterSelected] = useState([]);
    /** @type {[BidProfileManagement.IEmployeeAwardingResult[], Function]} */
    const [employeeAwardingResults, setEmployeeAwardingResults] = useState(awardingResults);
    const [sortColumn, setSortColumn] = useState('name');
    /** @type {[SortType, Function]} */
    const [sortType, setSortType] = useState(defaultSortType);
    /** @type {[boolean, Function]} */
    const [isApprovalActionDisabled, setIsApprovalActionDisabled] = useState(!!pendingTasks);
    /**
     * @type {[EmployeeReportType, Function]}
     */
    const [showReport, setShowReport] = useState(EmployeeReportType.None);
    const [showOnBehalfModal, setShowOnBehalfModal] = useState(false);
    const [onBehalfEmployeeId, setOnBehalfEmployeeId] = useState(0);
    const [isConfirmDialogOpen, setConfirmDialogOpen] = useState(false);

    const handleRecalculateAwarding = () => {
        dispatch(calculateAwarding(profileId, expandedRowKeys, items));
        setConfirmDialogOpen(false);
    };
    /**
     * 
     * @param {boolean} isOpen 
     */
    const setIsProgressSidePanelOpen = (isOpen) => {
        dispatch({
            type: UPDATE_WORKPLAN_PUBLISH_PROGRESS,
            payload: { isProgressSidePanelOpen: isOpen },
        });
    };

    /** @type {import('./ConfirmDialog').ConfirmDialogProps} */
    const refreshAwardingDialog = {
        title: t('manager.modal.refreshAwarding.title'),
        contentText: t('manager.modal.refreshAwarding.content'),
        cancelButtonText: t('manager.modal.options.no'),
        confirmButtonText: t('manager.modal.options.yes'),
        onCancelButtonClick: () => setConfirmDialogOpen(false),
        onConfirmButtonClick: () => handleRecalculateAwarding()
    };

    const [confirmDialog, setConfirmDialog] = useState(refreshAwardingDialog);
    /**
     * @type {[number|undefined, Function]}
     */
    const [awardingProgressValue, setAwardingProgressValue] = useState(undefined);
    /**
     * @type {[AwardEmployeesSteps, Function]}
     */
    const [awardingProgressStepCode, setAwardingProgressStepCode] = useState(AwardEmployeesSteps.Loading);
    const isSequential = originalBidProfileInformation.bidProcessType === BidProcessType.Sequential;
    const { sequentialSchedule } = originalSchedule;

    const { lockedEmployeeIds } = bidProfile.originalEmployees;

    useEffect(() => {
        setEmployeeAwardingResults(awardingResults);
    }, [JSON.stringify(awardingResults)]);

    useEffect(() => {
        setIsApprovalActionDisabled(!!pendingTasks);
    }, [JSON.stringify(pendingTasks)]);

    useEffect(() => {
        if (bidProfileManagement.isAwardingEmployees) {
            const interval = setInterval(async () => {
                const result = await api.getAwardingEmployeesProgressStatusQuery(bidProfile.id);
                if (result.status === 200) {
                    const { progress, stepCode, isComplete } = result.data;
                    /**
                     * @type {number | undefined}
                     */
                    let progressValue = progress;
                    if (isComplete) {
                        dispatch(updateAwardingCompletedBidProfile(bidProfile.id, expandedRowKeys, items));
                        setAwardingProgressStepCode(AwardEmployeesSteps.Loading);
                        setAwardingProgressValue(undefined);
                        return;
                    }
                    if (stepCode === AwardEmployeesSteps.WrappingUp) {
                        progressValue = undefined;
                    }
                    setAwardingProgressStepCode(stepCode);
                    setAwardingProgressValue(progressValue);
                }
            }, 5000);
            return () => clearInterval(interval);
        }
    }, [bidProfileManagement.isAwardingEmployees]);

    /**
     * @returns {BidProfileManagement.EmployeeSelectionsTableRowData[]}
     */
    const getSortedEmployeesBidSelection = () => {
        let result = employeeAwardingResults;
        if (sortColumn && sortType) {
            const sortOrder = sortType === 'asc' ? '' : '-';
            result = result.sort(
                dynamicSort(`${sortOrder}${sortColumn}`),
            );
        }

        if (statusFilterSelected.length) {
            result = result.filter((row) => statusFilterSelected.includes(row.status));
        }

        return result.map((x) => {
            const hasSequentialSchedule = isSequential && sequentialSchedule[x.employeeId];
            return ({
                employeeId: x.employeeId,
                rank: x.employee.rank,
                rankObjectPath: 'employee.rank',
                fullName: x.employee.fullName,
                fullNameObjectPath: 'employee.fullName',
                status: x.status,
                submittedOn: x.submittedOn,
                submittedBy: x.submittedBy,
                hasAwardedShiftline: x.hasAwardedShiftline,
                currentShiftLineAssignment: x.currentShiftLineAssignment,
                awardedShiftline: x.awardedShiftline,
                name: x.awardedShiftline?.schedulePatternTemplate?.name || '',
                employeeSubmissionStartDate: hasSequentialSchedule
                    ? sequentialSchedule[x.employeeId].employeeSubmissionStartDate : null,
                employeeSubmissionEndDate: hasSequentialSchedule
                    ? sequentialSchedule[x.employeeId].employeeSubmissionEndDate
                    : null,
                employeeSelectionIndex: x.employeeSelectionIndex,
                scheduledDateAndTime: getScheduledTime(sequentialSchedule[x.employeeId], hasSequentialSchedule),
                isLocked: x.isLocked,
            });
        });
    };

    /**
     * @returns {{ rank: number; employeeId: string; name: string; }[]}
     */
    const getAllEmployeeOptionsInFilter = () => {
        if (showReport === EmployeeReportType.EmployeeChoices) {
            return employeesBidSelection.map((x) => {
                const awardingResult = awardingResults.find((result) => result.employee.id === x.employeeId);
                return {
                    employeeId: x.employeeId,
                    name: awardingResult?.employee.fullName ?? '',
                    rank: awardingResult?.employee.rank ?? -1,
                };
            });
        }

        if (showReport === EmployeeReportType.EmployeeRanks) {
            return bidProfile.employees.rankedEmployees.filter((x) => !lockedEmployeeIds.includes(x.employeeId))
                .map((x) => ({
                    employeeId: x.employeeId,
                    rank: x.rank,
                    name: managerAppData.filteredDataviewEmployeeData
                        .find((y) => x.employeeId === y.employeeId)?.employeeName
                        ?? 'Employee not found',
                }));
        }

        return bidProfile.employees.rankedEmployees.map((x) => ({
            employeeId: x.employeeId,
            rank: x.rank,
            name: managerAppData.filteredDataviewEmployeeData
                .find((y) => x.employeeId === y.employeeId)?.employeeName
                ?? 'Employee not found',
        }));
    };

    const handleRefresh = () => {
        dispatch(
            refreshBidDashboard(profileId, {
                hyperfinds, managerBidSetups,
            }, BidProfileEditTab.Dashboard),
        );
    };

    const handleRecalculateEmployeeRanks = () => {
        setConfirmDialog({
            title: t('manager.modal.recalculateEmployeeRanks.title'),
            contentText: t('manager.modal.recalculateEmployeeRanks.content'),
            cancelButtonText: t('manager.modal.options.no'),
            confirmButtonText: t('manager.modal.options.yes'),
            onCancelButtonClick: () => setConfirmDialogOpen(false),
            onConfirmButtonClick: () => {
                dispatch(recalculateEmployeeRanks(profileId, items));
                setConfirmDialogOpen(false);
            }
        });
        setConfirmDialogOpen(true);
    };

    /** @param {number} employeeId */
    const handleOpenSingleEmployeePreferencesReport = async (employeeId) => {
        await dispatch({
            type: UPDATE_EMPLOYEE_PREFERENCES_REPORT,
            payload: {
                filters: {
                    ...filters,
                    selectedEmployees: [employeeId],
                    employeeOptionsInFilter: employeesBidSelection.map((x) => x.employeeId),
                },
            },
        });
        setShowReport(EmployeeReportType.EmployeeChoices);
    };

    /** @param {BidProfileManagement.EmployeeSelectionsTableRowData} rowData */
    const handleOpenOnBehalf = (rowData) => {
        setOnBehalfEmployeeId(rowData.employeeId);
        setShowOnBehalfModal(true);
    };

    /**
     * @param {{label: string, value: number, disabled: boolean}} option
     */
    const managerApprovalActionSelected = (option) => {
        if (!checkedKeys.length) {
            toastr.error('Please select at least one submission', '');
            return;
        }
        if (option.value === ManagerApprovalAction.Approve) {
            dispatch(approveBids(profileId, checkedKeys, items));
        } else if (option.value === ManagerApprovalAction.Refuse) {
            if (isSequential) {
                dispatch(refuseAndCalculate(profileId, checkedKeys, expandedRowKeys, items));
            } else {
                const toastrConfirmOptions = {
                    okText: 'Refuse and recalculate',
                    buttons: [{
                        text: 'Refuse only',
                        className: 'rrt-ok-btn',
                        handler: () => {
                            dispatch(refuseBids(profileId, checkedKeys, items));
                        },
                    },
                    {
                        ok: true,
                    },
                    {
                        cancel: true,
                    }],
                    onOk: async () => {
                        dispatch(refuseAndCalculate(profileId, checkedKeys, expandedRowKeys, items));
                    },
                    onCancel: () => { },
                    component: () => (
                        <div>
                            <DialogTitle style={{ padding: '16px 24px' }}>
                                Refuse
                            </DialogTitle>
                            <DialogContent>
                                <DialogContentText>
                                    Do you want to recalculate the results of the awards?
                                </DialogContentText>
                            </DialogContent>
                        </div>
                    ),
                };
                toastr.confirm('', toastrConfirmOptions);
            }
        }
    };

    /**
     * @param {string[]} selectedColumns
     */
    const handleDataviewFilterChange = (selectedColumns) => {
        const newColumnSelection = [...dataviewFields].map((x) => ({ ...x, checked: selectedColumns.includes(x.value) }));

        dispatch({
            type: UPDATE_MANAGER_APP_DATA,
            payload: { dataviewFields: newColumnSelection },
        });
    };

    const handleCloseOnBehalfDrawer = (showRefreshAwardingModal = false) => {
        setShowOnBehalfModal(false);
        setOnBehalfEmployeeId(0);
        if (showRefreshAwardingModal) {
            setConfirmDialog(refreshAwardingDialog);
            setConfirmDialogOpen(true);
        }
    };

    const handleCloseEmployeeReport = async () => {
        await dispatch({ type: RESET_EMPLOYEE_PREFERENCES_REPORT });
        setShowReport(EmployeeReportType.None);
    };

    const hasOnlyViewAccess = isViewAllowed && !isManagementAllowed;
    const isBidProfileActive = ![BidProfileStatus.Closed, BidProfileStatus.Published, BidProfileStatus.Canceled].includes(status);

    return (
        <div className="bdb-full-page">
            <Grid
                container
                justifyContent="space-between"
                className="bid-dashboard-header"
            >
                <Grid
                    item
                    xs={12}
                    lg={6}
                    className="manager-action-bar-left"
                >
                    {!hasOnlyViewAccess && (
                        <SplitButton
                            options={[
                                { label: tBidDashboard('approveBid'), value: ManagerApprovalAction.Approve, disabled: false },
                                { label: tBidDashboard('refuseBid'), value: ManagerApprovalAction.Refuse, disabled: false },
                            ]}
                            color="primary"
                            handleClick={(/** @type {SplitButtonOption} */ option) => managerApprovalActionSelected(option)}
                            disabled={isApprovalActionDisabled
                                || status !== BidProfileStatus.Closed
                                || isPublishingToWorkplan
                                || hasOnlyViewAccess}
                        />
                    )}
                    <Autocomplete
                        multiple
                        id="management-status-multiple-select"
                        className="management-status-multiple-select"
                        options={employeeSubmissionStatus}
                        limitTags={1}
                        disableCloseOnSelect
                        getOptionLabel={(option) => getEmployeeSubmissionStatusLabel(option)}
                        onChange={(event, values) => setStatusFilterSelected(values)}
                        renderOption={(option, { selected }) => (
                            <>
                                <Checkbox
                                    icon={icon}
                                    checkedIcon={checkedIcon}
                                    style={{ marginRight: 8 }}
                                    checked={selected}
                                    color="primary"
                                />
                                {getEmployeeSubmissionStatusLabel(option)}
                            </>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                size="small"
                                variant="outlined"
                                label={tBidDashboard('statusFilter')}
                            />
                        )}
                        renderTags={(value) => {
                            const numTags = value.length;
                            const limitTagsShown = 1;
                            return (
                                <Typography variant="body2">
                                    {value
                                        .slice(0, limitTagsShown)
                                        .map((option) => getEmployeeSubmissionStatusLabel(option))
                                        .join(', ')}

                                    {numTags > limitTagsShown && ` +${numTags - limitTagsShown} ${tBidDashboard('more')}`}
                                </Typography>
                            );
                        }}
                    />
                </Grid>
                <Grid
                    item
                    xs={12}
                    lg={3}
                    className="manager-action-bar-right subtle"
                >
                    {(isRecalculateEmployeeRanksAllowed && !hasOnlyViewAccess && !bidProfile.isArchive)
                        && (
                            <div className="bdb-select-button">
                                <Tooltip
                                    title={t('manager.bidProfileEdit.bidDashboard.recalculateEmployeeRanks')}
                                    arrow
                                    placement="bottom-end"
                                >
                                    <IconButton
                                        className="bdb-recalculate-employee-ranks-button"
                                        onClick={handleRecalculateEmployeeRanks}
                                        disabled={[BidProfileStatus.Canceled, BidProfileStatus.Published].includes(status)
                                            || isAwardingEmployees
                                            || hasOnlyViewAccess}
                                    >
                                        <MdiIcon
                                            path={mdiAccountReactivate}
                                            size={1}
                                        />
                                    </IconButton>
                                </Tooltip>
                            </div>
                        )}
                    {!bidProfile.isArchive
                        && (
                            <div className="bdb-select-button">
                                <Tooltip
                                    title={t('manager.bidProfileEdit.bidDashboard.refreshPage')}
                                    arrow
                                    placement="bottom-end"
                                >
                                    <IconButton
                                        className="bdb-refresh-button"
                                        onClick={handleRefresh}
                                    >
                                        <MdiIcon
                                            path={mdiRefresh}
                                            size={1}
                                        />
                                    </IconButton>
                                </Tooltip>
                            </div>
                        )}
                    <div className="bdb-select-button subtle">
                        <GroupedIconButton
                            icon={(
                                <MdiIcon
                                    path={mdiClipboardOutline}
                                    size={1}
                                />
                            )}
                            buttonClassName="bdb-report-button"
                            tooltipTitle={t('common.reports.reports')}
                            options={[
                                {
                                    value: EmployeeReportType.EmployeeChoices,
                                    label: t('common.reports.employeePreferences.title'),
                                    action: () => setShowReport(EmployeeReportType.EmployeeChoices),
                                    disabled: (!isSequential && ![BidProfileStatus.Active, BidProfileStatus.Closed, BidProfileStatus.Published].includes(status)),
                                },
                                {
                                    value: EmployeeReportType.UnawardedShiftlines,
                                    label: t('common.reports.unawardedShiftlines.title'),
                                    action: () => setShowReport(EmployeeReportType.UnawardedShiftlines),
                                    disabled: ![BidProfileStatus.Closed, BidProfileStatus.Published].includes(status),
                                },
                                {
                                    value: EmployeeReportType.EmployeeRanks,
                                    label: t('common.reports.employeeRanks.title'),
                                    action: () => setShowReport(EmployeeReportType.EmployeeRanks),
                                    disabled: false,
                                },
                                {
                                    value: EmployeeReportType.EmployeeBiddingResults,
                                    label: t('common.reports.biddingResults.title'),
                                    action: () => setShowReport(EmployeeReportType.EmployeeBiddingResults),
                                    disabled: ![BidProfileStatus.Closed, BidProfileStatus.Published].includes(status),
                                },
                            ]}
                        />
                    </div>
                    {
                        (!isSequential && !hasOnlyViewAccess && !bidProfile.isArchive) && (
                            <div className="bdb-select-button subtle">
                                <Tooltip
                                    title={
                                        !isAwardingEmployees ? t('manager.bidProfileEdit.bidDashboard.recalculateAwardingResults')
                                            : (
                                                <ZaddonsCircularProgress
                                                    size={25}
                                                    value={awardingProgressValue}
                                                    label={getAwardingStepCodeLabel(awardingProgressStepCode)}
                                                />
                                            )
                                    }
                                    arrow
                                    placement="bottom-end"
                                    classes={{ tooltip: !isAwardingEmployees ? '' : 'dbd-recalculating-awarding-tooltip' }}
                                >
                                    <div>
                                        <IconButton
                                            className="bdb-recalculate-button"
                                            onClick={handleRecalculateAwarding}
                                            disabled={
                                                isBidProfileActive
                                                || isPublishingToWorkplan
                                                || isAwardingEmployees
                                            }
                                        >
                                            <MdiIcon
                                                path={mdiAutorenew}
                                                size={1}
                                            />
                                        </IconButton>
                                    </div>
                                </Tooltip>
                            </div>
                        )
                    }
                    <Tooltip
                        title={t('manager.bidProfileEdit.bidDashboard.columnSelection')}
                        arrow
                    >
                        <div className="bdb-select-button column-selector">
                            <ColumnFilter
                                options={dataviewFields}
                                onOptionSelected={handleDataviewFilterChange}
                            />
                        </div>
                    </Tooltip>
                </Grid>
            </Grid>
            <Divider />
            <Grid
                container
                className="bdb-employee-table"
            >
                <div className="bdb-rtable-container">
                    <EmployeeSelectionsTable
                        data={getSortedEmployeesBidSelection()}
                        sortColumn={sortColumn}
                        setSortColumn={setSortColumn}
                        sortType={sortType}
                        setSortType={setSortType}
                        fullNameObjectPath="employee.fullName"
                        rankObjectPath="employee.rank"
                        openSingleEmployeePreferenceReport={(/** @type {number} */ employeeId) =>
                            handleOpenSingleEmployeePreferencesReport(employeeId)}
                        onBehalfSelected={(
                            /** @type {BidProfileManagement.EmployeeSelectionsTableRowData} */rowData,
                        ) => handleOpenOnBehalf(rowData)}
                        showScheduledDateAndTime={isSequential}
                    />
                </div>
            </Grid>
            <EmployeeReport
                profileId={profileId}
                employeeReport={showReport}
                onClose={handleCloseEmployeeReport}
                dataviewEmployeeData={dataviewEmployeeData}
                filteredDataviewEmployeeData={filteredDataviewEmployeeData}
                dataviewFields={dataviewFields}
                anchorDay={anchorDay}
                isRebid={isRebid}
                showScheduledDateAndTime={isSequential}
                sequentialSchedule={sequentialSchedule}
                dayCount={dayCount}
                isPeriodTypeDay={periodType === PeriodType.Days}
                lockedEmployeeIds={lockedEmployeeIds}
                employeeFilters={getAllEmployeeOptionsInFilter()}
            />
            <OnBehalfShiftlineSelection
                profileId={profileId}
                open={showOnBehalfModal}
                onClose={handleCloseOnBehalfDrawer}
                employeeId={onBehalfEmployeeId}
            />
            <ConfirmDialog
                isOpen={isConfirmDialogOpen}
                title={confirmDialog.title}
                contentText={confirmDialog.contentText}
                cancelButtonText={confirmDialog.cancelButtonText}
                confirmButtonText={confirmDialog.confirmButtonText}
                onCancelButtonClick={confirmDialog.onCancelButtonClick}
                onConfirmButtonClick={confirmDialog.onConfirmButtonClick}
            />
            <ProgressSidePanel
                open={isProgressSidePanelOpen}
                onClose={() => setIsProgressSidePanelOpen(false)}
                workplanName={workplanName}
                effectiveDate={effectiveDate}
                endDate={endDate}
                isForeverRequestPeriod={isForeverRequestPeriod}
                currentPublishStatusType={publishStatusType}
                currentProgressValue={progressValue}
                stepInfo={stepInfo}
            />
        </div>
    );
}

export default BidDashboard;
